import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Save, Truck, Building2, Loader2 } from 'lucide-react';
import { warehouseService } from '../services/warehouseService';

interface Ramp {
  id: string;
  warehouseId: string;
  warehouseName?: string;
  rampNumber: string;
  name: string;
  type: 'loading' | 'unloading' | 'both';
  capacity: number;
  height: number;
  width: number;
  hasLift: boolean;
  status: 'active' | 'maintenance' | 'inactive';
  notes?: string;
}

interface Warehouse {
  id: string;
  name: string;
  code: string;
}

export function RampManagement() {
  const [ramps, setRamps] = useState<Ramp[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Ramp | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<Ramp>>({
    type: 'both',
    hasLift: false,
    status: 'active',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [warehousesRes, rampsRes] = await Promise.all([
        warehouseService.getWarehouses(),
        warehouseService.getRamps()
      ]);

      if (warehousesRes.success && warehousesRes.data) {
        setWarehouses(warehousesRes.data);
      }

      if (rampsRes.success && rampsRes.data) {
        const sanitizedRamps = rampsRes.data.map((ramp: any) => ({
          ...ramp,
          status: ['active', 'maintenance', 'inactive'].includes(ramp.status) 
            ? ramp.status 
            : 'active'
        }));
        setRamps(enrichRampsWithWarehouseNames(sanitizedRamps, warehousesRes.data || []));
      } else {
        setRamps([]);
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const enrichRampsWithWarehouseNames = (rampsData: Ramp[], warehousesList: Warehouse[] = warehouses) => {
    return rampsData.map(ramp => ({
      ...ramp,
      warehouseName: warehousesList.find((w: Warehouse) => w.id === ramp.warehouseId)?.name || 'Bilinmeyen Depo'
    }));
  };

  const saveRamp = async () => {
    if (!formData.warehouseId || !formData.rampNumber || !formData.name) {
      alert('Lütfen zorunlu alanları doldurun');
      return;
    }

    const payload = {
      warehouseId: formData.warehouseId,
      rampNumber: formData.rampNumber,
      rampName: formData.name,
      rampType: formData.type,
      capacity: formData.capacity,
      height: formData.height,
      width: formData.width,
      hasLift: formData.hasLift,
      status: formData.status,
      notes: formData.notes,
    };

    setIsSaving(true);
    try {
      if (editingItem) {
        const response = await warehouseService.updateRamp(editingItem.id, payload);
        if (response.success) {
          await loadData();
        }
      } else {
        const response = await warehouseService.createRamp(payload);
        if (response.success) {
          await loadData();
        }
      }
      closeForm();
    } catch (err: any) {
      console.error('Error saving ramp:', err);
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRamp = async (id: string) => {
    if (confirm('Bu rampayı silmek istediğinizden emin misiniz?')) {
      try {
        const response = await warehouseService.deleteRamp(id);
        if (response.success) {
          await loadData();
        }
      } catch (err: any) {
        console.error('Error deleting ramp:', err);
        alert(err.message || 'Silme sırasında hata oluştu');
      }
    }
  };

  const openForm = (ramp?: Ramp) => {
    if (ramp) {
      setEditingItem(ramp);
      setFormData(ramp);
    } else {
      setEditingItem(null);
      setFormData({ type: 'both', hasLift: false, status: 'active', capacity: 24, height: 4.0, width: 3.0 });
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData({ type: 'both', hasLift: false, status: 'active' });
  };

  const filteredRamps = selectedWarehouse === 'all' 
    ? ramps 
    : ramps.filter(r => r.warehouseId === selectedWarehouse);

  const getTypeLabel = (type: Ramp['type']) => {
    const types = {
      loading: 'Yükleme',
      unloading: 'Boşaltma',
      both: 'Her İkisi'
    };
    return types[type];
  };

  const getStatusBadge = (status: Ramp['status']) => {
    const statuses: Record<string, { label: string; color: string }> = {
      active: { label: 'Aktif', color: 'bg-green-100 text-green-800' },
      maintenance: { label: 'Bakımda', color: 'bg-yellow-100 text-yellow-800' },
      inactive: { label: 'Pasif', color: 'bg-gray-100 text-gray-800' }
    };
    const config = statuses[status] || { label: 'Bilinmiyor', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rampa Yönetimi</h1>
          <p className="text-gray-500 mt-1">Depo rampalarını tanımlayın ve yönetin</p>
        </div>
        <button
          onClick={() => openForm()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Yeni Rampa Ekle
        </button>
      </div>

      {/* Loading/Error States */}
      {isLoading && (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-500">Rampalar yükleniyor...</p>
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-red-200">
          <div className="flex flex-col items-center gap-3 p-8">
            <p className="text-red-600 font-medium">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      )}

      {/* Filter */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Depo Filtrele</label>
          <select
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Depolar</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Ramps Grid */}
      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredRamps.map((ramp) => (
              <div key={ramp.id} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Truck className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{ramp.name}</h3>
                      <p className="text-sm text-gray-500">{ramp.rampNumber}</p>
                    </div>
                  </div>
                  {getStatusBadge(ramp.status)}
                </div>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Building2 className="w-4 h-4" />
                    <span className="font-medium">{ramp.warehouseName}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Tip:</span> {getTypeLabel(ramp.type)}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Kapasite:</span> {ramp.capacity} ton
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Boyutlar:</span> {ramp.height}m (Y) × {ramp.width}m (G)
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Hidrolik Lift:</span> {ramp.hasLift ? '✓ Var' : '✗ Yok'}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => openForm(ramp)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                  >
                    <Edit className="w-4 h-4" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => deleteRamp(ramp.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                    Sil
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredRamps.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Henüz rampa eklenmemiş</p>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Rampa Düzenle' : 'Yeni Rampa Ekle'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Depo *
                  </label>
                  <select
                    value={formData.warehouseId || ''}
                    onChange={(e) => setFormData({ ...formData, warehouseId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Depo Seçin</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rampa Numarası *
                  </label>
                  <input
                    type="text"
                    value={formData.rampNumber || ''}
                    onChange={(e) => setFormData({ ...formData, rampNumber: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="R1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rampa Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Rampa 1 - Hızlı Yükleme"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tip
                  </label>
                  <select
                    value={formData.type || 'both'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Ramp['type'] })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="loading">Yükleme</option>
                    <option value="unloading">Boşaltma</option>
                    <option value="both">Her İkisi</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kapasite (ton)
                  </label>
                  <input
                    type="number"
                    value={formData.capacity || ''}
                    onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="24"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yükseklik (m)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.height || ''}
                    onChange={(e) => setFormData({ ...formData, height: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="4.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Genişlik (m)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.width || ''}
                    onChange={(e) => setFormData({ ...formData, width: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="3.0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as Ramp['status'] })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Aktif</option>
                    <option value="maintenance">Bakımda</option>
                    <option value="inactive">Pasif</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasLift || false}
                      onChange={(e) => setFormData({ ...formData, hasLift: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Hidrolik Lift Var</span>
                  </label>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notlar
                  </label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ek bilgiler"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row">
                <button
                  onClick={closeForm}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  İptal
                </button>
                <button
                  onClick={saveRamp}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Save className="w-4 h-4" />
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
